import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/eac6a9d6-a2b8-4138-ac54-4df533182db6.png" 
              alt="ARTOS d.o.o. Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className={`text-lg font-medium transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              ARTOS d.o.o.
            </span>
          </div>
          <div className="flex gap-8">
            {['home', 'about', 'products', 'features', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className={`text-sm font-normal capitalize transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1f3a] via-[#2d1b4e] to-[#1a1f3a]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="relative z-10 text-center px-16 w-full">
          <h1 className="text-7xl font-extralight text-white tracking-wider leading-tight mb-8">
            Premium iOS Applications
            <br />
            for Modern Users
          </h1>
          <p className="text-xl font-light text-white/70 max-w-3xl mx-auto">
            Enhancing mobile security and user experience through innovative VPN and Cleaner solutions
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-16 bg-[#F8F7F5]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <p className="text-xs uppercase tracking-widest text-[#6B7B5E] mb-6 font-medium">WHO WE ARE</p>
            <div className="flex items-center gap-6 mb-20">
              <h2 className="text-6xl font-semibold text-[#2A2A2A] leading-tight">
                Expert Team Building
                <br />
                <span className="inline-flex items-center gap-6">
                  <span>Modern</span>
                  <img 
                    src="https://public.readdy.ai/ai/img_res/670b24f5f2e7e1dae87f0819fddcbe19.jpg" 
                    alt="VPN Interface" 
                    className="w-28 h-28 rounded-full object-cover inline-block"
                  />
                  <span>iOS Solutions</span>
                </span>
                <br />
                <span className="inline-flex items-center gap-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20application%20dashboard%20with%20green%20accents%20showing%20storage%20optimization%20interface%2C%20clean%20minimalist%20design%20with%20circular%20progress%20indicators%2C%20white%20background%20with%20green%20success%20elements%2C%20professional%20mobile%20app%20screenshot&width=120&height=120&seq=cleaner-about-1&orientation=squarish" 
                    alt="Cleaner Dashboard" 
                    className="w-28 h-28 rounded-full object-cover inline-block"
                  />
                  <span>Since Foundation</span>
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20iOS%20app%20icon%20design%20with%20gradient%20colors%2C%20rounded%20square%20shape%20with%20professional%20branding%2C%20clean%20minimalist%20style%2C%20premium%20mobile%20application%20logo%20on%20white%20background&width=120&height=120&seq=app-icon-about-1&orientation=squarish" 
                    alt="App Icon" 
                    className="w-28 h-28 rounded-full object-cover inline-block"
                  />
                </span>
              </h2>
            </div>
            <p className="text-lg text-[#6B6B6B] leading-relaxed">
              ARTOS d.o.o. is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-16 bg-[#F5F3EE]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-wide text-[#8B8B8B] font-light mb-4">OUR EXPERTISE</p>
          <h2 className="text-6xl font-extrabold text-black leading-tight mb-24">
            Core Technologies &
            <br />
            Product Features
          </h2>
          <div className="grid grid-cols-3 gap-8">
            <div className="bg-[#EEEDE9] rounded-3xl p-10">
              <div className="w-12 h-12 flex items-center justify-center mb-8">
                <i className="ri-shield-line text-5xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-5">Secure VPN Connection</h3>
              <p className="text-base text-[#6B6B6B] leading-relaxed">
                Advanced encryption protocols ensure your online privacy and data protection. Our VPN technology provides secure tunneling and anonymous browsing across all networks.
              </p>
            </div>
            <div className="bg-[#EEEDE9] rounded-3xl p-10">
              <div className="w-12 h-12 flex items-center justify-center mb-8">
                <i className="ri-brush-line text-5xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-5">System Optimization</h3>
              <p className="text-base text-[#6B6B6B] leading-relaxed">
                Intelligent storage management and performance enhancement tools keep your device running smoothly. Remove junk files and optimize system resources effortlessly.
              </p>
            </div>
            <div className="bg-[#EEEDE9] rounded-3xl p-10">
              <div className="w-12 h-12 flex items-center justify-center mb-8">
                <i className="ri-smartphone-line text-5xl text-black"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-5">Intuitive Interface</h3>
              <p className="text-base text-[#6B6B6B] leading-relaxed">
                User-friendly design with seamless navigation makes complex features accessible to everyone. Experience premium functionality with minimal learning curve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="text-5xl font-extralight text-[#3A3A3A] mb-5">Our iOS Applications</h2>
            <p className="text-base text-gray-400">Designed for security and performance</p>
          </div>
          <div className="grid grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-[#E8F4F8] rounded-2xl p-12 m-4 flex items-center justify-center" style={{ aspectRatio: '1/1' }}>
                <img 
                  src="https://readdy.ai/api/search-image?query=premium%20iOS%20VPN%20application%20interface%20mockup%20with%20blue%20gradient%20background%2C%20showing%20connection%20toggle%20and%20server%20selection%20screen%2C%20modern%20minimalist%20design%20with%20shield%20icon%20and%20secure%20connection%20indicators%2C%20professional%20mobile%20app%20UI%20on%20simple%20light%20blue%20backdrop&width=400&height=500&seq=vpn-product-1&orientation=portrait" 
                  alt="VPN Application" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-semibold text-[#2A2A2A] mb-2">VPN Security</h3>
                <p className="text-3xl font-bold text-[#1A1A1A] mb-6">Premium Protection</p>
                <p className="text-xs text-[#ADADAD] mt-4">iOS 14.0 or later</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-[#E8F5E9] rounded-2xl p-12 m-4 flex items-center justify-center" style={{ aspectRatio: '1/1' }}>
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20application%20dashboard%20mockup%20with%20green%20accents%2C%20showing%20storage%20analysis%20with%20circular%20progress%20charts%20and%20cleanup%20options%2C%20clean%20minimalist%20interface%20with%20white%20background%20and%20green%20success%20elements%2C%20professional%20mobile%20optimization%20app%20UI%20on%20light%20green%20backdrop&width=400&height=500&seq=cleaner-product-1&orientation=portrait" 
                  alt="Cleaner Application" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-semibold text-[#2A2A2A] mb-2">System Cleaner</h3>
                <p className="text-3xl font-bold text-[#1A1A1A] mb-6">Smart Optimization</p>
                <p className="text-xs text-[#ADADAD] mt-4">iOS 14.0 or later</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-[#F3E8F8] rounded-2xl p-12 m-4 flex items-center justify-center" style={{ aspectRatio: '1/1' }}>
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iOS%20application%20interface%20mockup%20with%20purple%20gradient%20background%2C%20showing%20app%20features%20and%20settings%20screen%2C%20elegant%20minimalist%20design%20with%20smooth%20transitions%2C%20professional%20mobile%20app%20UI%20on%20soft%20lavender%20backdrop&width=400&height=500&seq=app-product-1&orientation=portrait" 
                  alt="Additional Application" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-semibold text-[#2A2A2A] mb-2">Performance Suite</h3>
                <p className="text-3xl font-bold text-[#1A1A1A] mb-6">Complete Solution</p>
                <p className="text-xs text-[#ADADAD] mt-4">iOS 14.0 or later</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-16 bg-[#2B2B2B]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-4 gap-12 mb-32">
            <div>
              <h3 className="text-sm uppercase tracking-wide text-white font-bold mb-6 border-b border-white/20 pb-2">LOCATION</h3>
              <div className="space-y-3 text-base text-[#E0E0E0]">
                <p className="flex items-center gap-3">
                  <i className="ri-map-pin-line text-xl"></i>
                  <span>Lukac 94a</span>
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-building-line text-xl"></i>
                  <span>City Lukac</span>
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-mail-line text-xl"></i>
                  <span>Zip 33406</span>
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-global-line text-xl"></i>
                  <span>Country Croatia</span>
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wide text-white font-bold mb-6 border-b border-white/20 pb-2">CONTACT</h3>
              <div className="space-y-3 text-base text-[#E0E0E0]">
                <p className="flex items-center gap-3">
                  <i className="ri-mail-line text-xl"></i>
                  <span>info@artosdoo.com</span>
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-phone-line text-xl"></i>
                  <span>+1 2522976312</span>
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wide text-white font-bold mb-6 border-b border-white/20 pb-2">COMPANY</h3>
              <p className="text-sm text-[#C0C0C0] leading-relaxed">
                Expert team developing modern iOS applications focused on security, performance, and user experience.
              </p>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wide text-white font-bold mb-6 border-b border-white/20 pb-2">CONNECT</h3>
              <form 
                id="contact-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d6apkclbd17lru29lvi0"
                method="POST"
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  fetch('https://readdy.ai/api/form/d6apkclbd17lru29lvi0', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString(),
                  })
                    .then(() => {
                      alert('Message sent successfully!');
                      form.reset();
                    })
                    .catch(() => {
                      alert('Failed to send message. Please try again.');
                    });
                }}
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  required
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 text-sm focus:outline-none focus:border-white/40"
                />
                <textarea
                  name="message"
                  placeholder="Your Message"
                  maxLength={500}
                  required
                  rows={3}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 text-sm focus:outline-none focus:border-white/40 resize-none"
                ></textarea>
                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-white text-[#2B2B2B] rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
          <div className="flex items-end justify-between pt-12 border-t border-white/10">
            <div>
              <div className="flex items-baseline gap-3 mb-4">
                <h2 className="text-8xl font-extrabold text-white">ARTOS</h2>
                <span className="text-6xl font-extralight text-white">d.o.o.</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-16 h-0.5 bg-white"></div>
                <p className="text-xs text-[#8B8B8B]">© 2025 ARTOS d.o.o. All rights reserved.</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#9B9B9B] mb-6">Made with precision</p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-[#8B8B8B] hover:text-white transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
